import { createEnchantmentCompilationContext } from '../compile/enchantment-template-context';
import { readItemNumericSlot } from '../compile/enchantment-template';
import { createFormulaReadContext } from '../compile/formula-read-context';
import type { ItemFieldRow } from '../compile/item-field-reader';
import type { ProviderCatalogRows } from '../compile/provider-catalog-order';
import { readItemUuidSlot, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { computeRequiredItemCharges } from '../port/k1-compile/db/enchantment_templates/compute_required_item_charges';
import type { PortEnv } from '../port-contract';
import { enchantmentPartEligible } from './enchantment-part-eligibility';
export { enchantmentPartEligible } from './enchantment-part-eligibility';

export type Row = Record<string, unknown>;
export const PART_SLOTS = ['innate_spell_per_long_rest', 'at_will_spell', 'charged_spell', 'charged_ability',
    'passive_ability', 'attack_ability', 'opportunity_attack_ability', 'trap_ability'] as const;
export type PartSlot = typeof PART_SLOTS[number];
export type Selections = Partial<Record<PartSlot | 'item_charges' | 'item_rechargeable', string[]>>;
const MEMBER_ORDER: readonly (keyof Selections)[] = [...PART_SLOTS.slice(0, 2), 'item_charges',
    ...PART_SLOTS.slice(2, 4), 'item_rechargeable', ...PART_SLOTS.slice(4)];
export interface AuthoringFacts {
    schema: 'enchantment-authoring-inputs.v1';
    definitions: Record<string, Row>;
    candidates: Row[];
    templates: Row[];
    members: Row[];
    viewer?: { id: string; staff: boolean };
    metadata: { layerCatalog: ProviderCatalogRows & Readonly<Record<string, { aggregation_strategy?: string }>>;
        abilityTypes: string[]; dynamicLayers: string[]; spellCosts: number[] };
}
export const uuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/** One request's native readers, over the same authored closure used by C5. No compile/cache owner. */
export function authoringReaders(facts: AuthoringFacts) {
    const definition = (id: string): Row | null => facts.definitions[id.toUpperCase()] ?? facts.definitions[id] ?? null;
    const read = createFormulaReadContext({ catalogRows: facts.metadata.layerCatalog, abilityTypes: facts.metadata.abilityTypes,
        aggregationStrategy: name => facts.metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, queryTemplateDefinition: definition, resolveInventoryInstance: () => null });
    const entries = (row: ItemFieldRow | null, key: string): unknown[] => Object.values(read.layerEntries(row, key));
    const has = (row: ItemFieldRow | null, key: string): boolean => read.layerValue(row, key) !== null || entries(row, key).length > 0;
    const id = (cell: unknown): string => (readItemUuidSlot(cell) || readLayerListEntryUuid(cell)).toUpperCase();
    const number = (row: ItemFieldRow | null, key: string, fallback = 0): number => readItemNumericSlot(read.layerValue(row, key), fallback);
    const name = (row: ItemFieldRow | null): string => String(read.itemField(row, 'item_name') ?? '');
    return { ...read, definition, entries, has, id, number, name };
}
export type AuthoringReaders = ReturnType<typeof authoringReaders>;

export function catalogPartVisible(part: Row, facts: AuthoringFacts, read: AuthoringReaders): boolean {
    if (Number(read.itemField(part, 'item_deleted') ?? 0)) return false;
    return !Number(read.itemField(part, 'item_private') ?? 0) || facts.viewer?.staff === true
        || !!facts.viewer?.id && String(read.itemField(part, 'item_created_by_id') ?? '').toUpperCase() === facts.viewer.id.toUpperCase();
}

const axes = ['item_type', 'equipment_slot', 'weapon_classification', 'weapon_type', 'weapon_category',
    'armor_type', 'armor_classification', 'instrument_classification'] as const;
export function pickerCompatible(base: Row, part: Row, read: AuthoringReaders): boolean {
    const baseType = read.itemField(base, 'item_type');
    const value = (axis: typeof axes[number]): unknown => axis === 'item_type' ? baseType
        : axis === 'equipment_slot' ? read.layerValue(base, axis) ?? '4FF85144-67EC-9D87-AE28-15137A1A2481'
        : axis.startsWith('weapon_') && baseType !== 'weapon' || axis.startsWith('armor_') && baseType !== 'armor'
            || axis.startsWith('instrument_') && baseType !== 'instrument' ? null : read.layerValue(base, axis);
    const matches = (entry: unknown, expected: unknown): boolean => String(read.id(entry) || entry).toUpperCase()
        === String(read.id(expected) || expected).toUpperCase();
    const included = axes.filter(axis => read.has(part, 'enchantment_' + axis));
    return (!included.length || included.some(axis => read.entries(part, 'enchantment_' + axis).some(entry => matches(entry, value(axis)))))
        && !axes.some(axis => read.entries(part, 'enchantment_not_' + axis).some(entry => matches(entry, value(axis))));
}
export function pickerMembers(base: Row, facts: AuthoringFacts): Record<PartSlot, string[]> {
    const read = authoringReaders(facts);
    const out = Object.fromEntries(PART_SLOTS.map(slot => [slot, []])) as unknown as Record<PartSlot, string[]>;
    for (const candidate of facts.candidates) {
        const id = String(candidate.item_id), part = read.definition(id);
        if (!part || !catalogPartVisible(part, facts, read) || !enchantmentPartEligible(part, read)) continue;
        const type = String(read.itemField(part, 'item_type'));
        if (type === 'spell') {
            for (const slot of ['innate_spell_per_long_rest', 'at_will_spell', 'charged_spell'] as const) out[slot].push(id);
        } else if (type === 'trap_ability') out.trap_ability.push(id);
        else if (pickerCompatible(base, part, read)) {
            if (type === 'passive_ability' && ['cost_gp', 'cost_gp_multiplier', 'weight_multiplier'].some(key => read.has(part, key))) out.passive_ability.push(id);
            if ((type === 'attack_ability' || type === 'opportunity_attack_ability') && read.has(part, 'cost_gp')) out[type].push(id);
            if (['quick_ability', 'ability', 'full_ability', 'reaction_ability', 'opportunity_attack_ability', 'attack_ability', 'free_ability'].includes(type)
                && read.has(part, 'ability_activation_charges')) out.charged_ability.push(id);
        }
    }
    return out;
}
export function remixSelections(members: Row[], facts: AuthoringFacts): { selections: Selections; warnings: string[] } {
    const read = authoringReaders(facts), selections: Selections = {}, warnings: string[] = [];
    for (const member of members) {
        if (Number(member.enchantment_template_enchantment_deleted ?? 0)) continue;
        const slot = String(member.enchantment_type) as keyof Selections, value = String(member.enchantment_item_id ?? '');
        if (slot === 'item_charges' || slot === 'item_rechargeable') { selections[slot] = [value]; continue; }
        if (!PART_SLOTS.includes(slot)) continue;
        const part = read.definition(value);
        if (!part) throw new Error(`unported:enchantment-authoring:missing-remix-part:${value}`);
        if (!catalogPartVisible(part, facts, read)) { warnings.push(`Unavailable catalog part (${slot}) — removed from this remix`); continue; }
        if (!enchantmentPartEligible(part, read)) { warnings.push(`${read.name(part) || value} (${slot}) — no longer enchantable`); continue; }
        (selections[slot] ??= []).push(value);
    }
    if (!selections.charged_spell?.length && !selections.charged_ability?.length) {
        delete selections.item_charges; delete selections.item_rechargeable;
    }
    return { selections, warnings };
}

export function quoteEnchantment(base: Row, selected: Selections, facts: AuthoringFacts) {
    const read = authoringReaders(facts), selections: Selections = structuredClone(selected);
    for (const slot of PART_SLOTS) for (const id of selections[slot] ?? []) {
        const part = read.definition(id);
        if (!part) throw new Error(`unported:enchantment-authoring:missing-part:${id}`);
        if (!catalogPartVisible(part, facts, read)) throw new Error('The selected enchantment part is unavailable in your catalog.');
        if (!enchantmentPartEligible(part, read)) throw new Error(`${read.name(part)} cannot be an enchantment part.`);
    }
    const charged = !!(selections.charged_spell?.length || selections.charged_ability?.length);
    const env = { deps: { valid_uuid(id: unknown) { if (!uuid(id)) throw new Error('Invalid enchantment reference'); },
        query_full_item_arr: (id: unknown) => read.definition(String(id)),
        layer_value: (row: unknown, key: string) => read.layerValue(row as ItemFieldRow | null, key) } } as unknown as PortEnv<Parameters<typeof computeRequiredItemCharges>[2]['deps']>;
    const required = charged ? Math.max(1, Number(computeRequiredItemCharges(selections.charged_ability ?? [], selections.charged_spell ?? [], env))) : 0;
    const charges = charged ? Math.max(required, Math.trunc(Number(selections.item_charges?.[0]) || 0)) : 0;
    const rechargeable = charged && selections.item_rechargeable?.[0] !== '0';
    delete selections.item_charges; delete selections.item_rechargeable;
    if (charged) selections.item_charges = [String(charges)];
    if (rechargeable) selections.item_rechargeable = ['1'];
    const costs: Record<string, number> = {}, multipliers: Record<string, number> = {}, subtotals: Record<string, number> = {};
    for (const slot of PART_SLOTS) {
        costs[slot] = (selections[slot] ?? []).reduce((sum, id) => {
            const part = read.definition(id);
            return sum + (slot.includes('spell') ? facts.metadata.spellCosts[Math.trunc(read.number(part, 'spell_level'))] ?? 0
                : slot === 'charged_ability' ? read.number(part, 'ability_activation_charges') * 50 : read.number(part, 'cost_gp'));
        }, 0);
        multipliers[slot] = slot === 'innate_spell_per_long_rest' ? 3 : slot === 'at_will_spell' ? 10
            : slot === 'charged_spell' || slot === 'charged_ability' ? rechargeable ? 3 : 1 : 1;
        subtotals[slot] = Math.round(costs[slot]! * multipliers[slot]!);
    }
    const chargeMultiplier = rechargeable ? charges >= 20 ? .8 : charges >= 10 ? .9 : 1 : 0;
    subtotals.item_charges = Math.round(charges * chargeMultiplier * 50);
    const multiplierSum = (selections.passive_ability ?? []).reduce((sum, id) => sum + read.number(read.definition(id), 'cost_gp_multiplier'), 0);
    const multiplier = Math.round((multiplierSum || 1) * 100) / 100;
    const baseCost = read.number(base, 'cost_gp');
    // The native template builder is also the runtime serialization authority. Its total_cost is
    // separate from this form's historical quote (which includes the host and overcharge discount).
    const templateId = '00000000-0000-0000-0000-000000000001';
    const members = Object.entries(selections).flatMap(([type, values]) => values!.map(id => ({ enchantment_type: type,
        enchantment_item_id: id, item_name: uuid(id) ? read.name(read.definition(id)) : null })));
    const native = createEnchantmentCompilationContext({ catalogRows: facts.metadata.layerCatalog,
        abilityTypes: facts.metadata.abilityTypes, dynamicLayers: facts.metadata.dynamicLayers, spellCosts: facts.metadata.spellCosts,
        aggregationStrategy: key => facts.metadata.layerCatalog[key]?.aggregation_strategy ?? 'host_wins',
        templateRows: () => [{ enchantment_template_id: templateId }], memberRows: () => members,
        fullItem: read.definition, resolveCarrierRef: read.definition, resolveInventoryInstance: () => null }).buildTemplate(templateId);
    return { selections, required, charges, rechargeable, costs, multipliers, subtotals, chargeMultiplier, multiplier, baseCost,
        total: (baseCost + Object.values(subtotals).reduce((a, b) => a + b, 0)) * multiplier, native,
        name: members.length === 1 ? String(members[0]!.item_name ?? '') : '' };
}
export function templateCommand(id: string, quote: ReturnType<typeof quoteEnchantment>) {
    if (!PART_SLOTS.some(slot => quote.selections[slot]?.length)) throw new Error('No enchantments applied!');
    return { schema: 'enchantment-authoring.v1', kind: 'template', template_id: id, name: quote.name,
        members: MEMBER_ORDER.flatMap(type => (quote.selections[type] ?? []).map(value => ({ kind: 'member', type, value }))) };
}

/** A page load/quote may finish after a newer selection. Only the latest generation may paint. */
export function latestAuthoringRequest() {
    let generation = 0;
    return { begin: () => ++generation, current: (value: number) => value === generation, retire: () => { ++generation; } };
}
